import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import Testimonials from '../content/Testimonials';
import CateringFaq from '../faqs/CateringFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

const featuredTrucks = { featured: true };

function EventCatering() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div className='CateringPage'>
      <Meta
        title={`Food ${vehicle_type}s for Events`}
        type='website'
        canonical='/food-truck-catering/events'
        image='https://static.seattlefoodtruck.com/photos/catering-header.webp'
        description={`Book food ${vehicleType}s for events in ${city_name}. Corporate events, weddings, parties, festivals, fundraisers & more. Get free quotes from ${city_name}'s best food ${vehicleType}s.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Event Catering in ${city_name}`,
            description: `Book food ${vehicleType}s for events in ${city_name}. Corporate events, weddings, parties, festivals, fundraisers and more.`,
            url: '/food-truck-catering/events',
            serviceType: 'Food Truck Event Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Events', item: '/food-truck-catering/events' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering-header.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Book Food {vehicle_type}s for Any Event</h1>
            <p>From corporate gatherings and weddings to birthday parties, festivals, and fundraisers, food {vehicleType}s bring the perfect blend of convenience, variety, and excitement to any occasion. {site_title} makes it easy to browse {city_name}&#39;s top-rated food {vehicleType}s, compare menus, and book catering for your next event — all in just a few clicks.</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Get Free Event Catering Quotes</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <FeaturedTrucks title={`Popular Food ${vehicle_type}s for Events`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Book a Food ${vehicle_type} for Your Event?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>Food {vehicle_type}s for Every Type of Event</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering2.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Corporate Events & Team Outings</h3>
            <p>Food {vehicleType}s are a hit at company picnics, team-building events, product launches, and employee appreciation days. Offer your team a range of cuisines — from gourmet tacos and wood-fired pizza to fresh poke bowls and craft burgers — all served fresh and made to order. It&#39;s catering that feels like a treat, not just a meal.</p>
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Weddings, Parties & Celebrations</h3>
            <p>Whether it&#39;s a wedding reception, birthday bash, graduation party, or anniversary celebration, food {vehicleType}s add a unique, memorable touch. Guests love the interactive experience of ordering from a {vehicleType}, and hosts love the simplicity — no need for a full catering setup, just great food served on the spot.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/community.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Festivals, Fundraisers & Community Events</h3>
            <p>Planning a neighborhood block party, charity fundraiser, school festival, or community gathering? Food {vehicleType}s are a natural fit. They draw crowds, keep lines moving, and offer something for everyone. Many {vehicleType}s in {city_name} have experience working large-scale events and can handle high volume with ease.</p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent />

      <Testimonials title="See what our customers are saying" />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta title={`Book a Food ${vehicle_type} for Your Event!`} />
      </section>
    </div>
  );
}

export default EventCatering;
